import { useEffect, useRef, useState } from "react";
import { PriorityBadge, TaskStatusBadge } from "@/components/TaskBadges";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Circle, CheckCircle2, CalendarDays, Flag, Pencil, Trash2, Undo2 } from "lucide-react";
import { deadlineLabel, groupTasks } from "./taskUtils";

// Obsah řádku úkolu — sdílený mezi ListRow (počítač) a SwipeRow (mobil), ať
// nevzniknou tři skoro stejné komponenty (zadání 92/E). Tři řádky pod sebou,
// název přes celou šířku (na 390 px se neusekává na „Poptat sub…").
function RowBody({ t, done, isDelegated }) {
  const dl = deadlineLabel(t);
  return (
    <div className="min-w-0 flex-1 space-y-1">
      <div className="font-bold text-[15px] leading-snug line-clamp-2">{t.title}</div>
      <div className="text-xs text-muted-foreground truncate">
        {[t.project_name, (t.assignee_users || []).map((a) => a.name).join(", ")].filter(Boolean).join(" · ") || "—"}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <PriorityBadge value={t.priority} />
        {done
          ? <span className="text-xs tabular-nums text-success">hotovo {formatDate(t.completion?.at || t.deadline)}</span>
          : (dl && <span className={`text-xs tabular-nums whitespace-nowrap ${dl.cls}`}>{dl.text}</span>)}
        {isDelegated(t) && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase">Zadal jsem</span>}
      </div>
    </div>
  );
}

export function ListRow({ t, done, onOpen, onComplete, onReopen, canComplete, canReopen, isDelegated }) {
  return (
    <div onClick={() => onOpen(t.id)} data-testid={`list-row-${t.id}`}
      className="flex items-start gap-3 px-4 py-3.5 border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer transition-colors">
      {!done && (
        <button onClick={(e) => { e.stopPropagation(); onComplete(t, e.currentTarget); }} disabled={!canComplete}
          data-testid={`list-complete-${t.id}`} title={canComplete ? "Označit jako hotovo" : "Dokončí řešitel"}
          className={`shrink-0 self-start mt-0.5 ${canComplete ? "text-muted-foreground hover:text-success" : "text-border cursor-default"}`}>
          <Circle size={20} />
        </button>
      )}
      <RowBody t={t} done={done} isDelegated={isDelegated} />
      {done ? (
        <div className="flex items-center gap-2 shrink-0 self-center">
          <TaskStatusBadge value={t.status} />
          {canReopen && (
            <Button variant="outline" size="sm" data-testid={`list-reopen-${t.id}`}
              onClick={(e) => { e.stopPropagation(); onReopen(t); }} className="h-9"><Undo2 size={15} /> Vrátit</Button>
          )}
        </div>
      ) : <ChevronRight size={16} className="text-muted-foreground shrink-0 self-center" />}
    </div>
  );
}

const prefersReducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const ACTION_W = 52;

// Řádek, který jde tahat prstem (zadání 90/92/101). Doprava = dokončit
// (v Historii vrátit „Zpět"), doleva = řádek se zastaví na pásu akcí,
// přidržení = mazání. Odemčený je vždy jen jeden řádek (`openId` na stránce).
export function SwipeRow({ t, done, onOpen, onComplete, onReopen, onToday, onPriority, onEdit, onDelete, canEdit, canIdentity, canDelete, canReopen, openId, setOpenId, isDelegated }) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const rowRef = useRef(null);
  const startX = useRef(0);
  const hold = useRef(null);
  const captured = useRef(false);
  const justSwiped = useRef(false);
  const isOpen = openId === t.id;

  // V Historii se needituje ani nedokončuje — vlevo jen mazání (když smí).
  // Dnes/Priorita = třídění (smí i vedení, canEdit); Upravit = zadání (canIdentity).
  const actions = done
    ? (canDelete ? [{ key: "del", label: "Smazat", Icon: Trash2, cls: "bg-destructive text-white", fn: () => onDelete(t) }] : [])
    : [
        ...(canEdit ? [
          { key: "today", label: "Dnes", Icon: CalendarDays, cls: "bg-card text-foreground", fn: () => onToday(t) },
          { key: "prio", label: "Priorita", Icon: Flag, cls: "bg-card text-foreground", fn: () => onPriority(t) },
        ] : []),
        ...(canIdentity ? [{ key: "edit", label: "Upravit", Icon: Pencil, cls: "bg-brand/15 text-brand", fn: () => onEdit(t) }] : []),
        ...(canDelete ? [{ key: "del", label: "Smazat", Icon: Trash2, cls: "bg-destructive text-white", fn: () => onDelete(t) }] : []),
      ];
  const hasActions = actions.length > 0;
  const openW = actions.length * ACTION_W;
  const canRight = done ? canReopen : true;              // tah doprava: dokončit / vrátit
  const fromRest = useRef(true);   // začínal tah v základní poloze (ne v odemčeném)?
  const base = useRef(0);          // odkud řádek startoval: 0 nebo -openW

  useEffect(() => { if (!isOpen && !dragging) setDx(0); }, [isOpen, dragging]);

  const clearHold = () => { if (hold.current) { clearTimeout(hold.current); hold.current = null; } };
  const markSwiped = () => { justSwiped.current = true; setTimeout(() => { justSwiped.current = false; }, 400); };

  const onDown = (e) => {
    fromRest.current = !isOpen;
    base.current = isOpen ? -openW : 0;
    startX.current = e.clientX; captured.current = false; setDragging(true);
    clearHold();
    // Přidržení na mazání jen na řádku v klidu, ne na odemčeném.
    if (canDelete && fromRest.current) hold.current = setTimeout(() => { hold.current = null; setDragging(false); setDx(0); setOpenId(null); markSwiped(); onDelete(t); }, 550);
  };
  const onMove = (e) => {
    if (!dragging) return;
    let d = base.current + (e.clientX - startX.current);
    if (Math.abs(e.clientX - startX.current) > 6) {
      clearHold();
      if (!captured.current) { try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ } captured.current = true; }
    }
    if (!canRight && d > 0) d = 0;                        // doprava nedělá nic (Historie bez práva vrátit)
    d = Math.max(hasActions ? -openW : 0, Math.min(160, d));   // doleva max po pás akcí
    setDx(d);
  };
  const onUp = () => {
    clearHold();
    if (!dragging) return;
    setDragging(false);
    const d = dx;
    if (Math.abs(d - base.current) > 6) markSwiped();
    // Zavírání odemčeného řádku nesmí nic spustit — ani otevřít detail klepnutím.
    if (!fromRest.current) { markSwiped(); setDx(0); setOpenId(null); return; }
    if (d >= 90 && canRight) { setDx(0); setOpenId(null); (done ? onReopen : onComplete)(t); return; }
    if (hasActions && d <= -Math.max(28, openW * 0.35)) { setDx(-openW); setOpenId(t.id); return; }
    setDx(0); if (isOpen) setOpenId(null);
  };
  const handleClick = () => {
    if (justSwiped.current) return;
    if (isOpen) { setOpenId(null); setDx(0); return; }
    onOpen(t.id);
  };

  const noAnim = dragging || prefersReducedMotion();

  return (
    <div className="relative overflow-hidden rounded-[16px] bg-secondary" data-testid={`swipe-row-${t.id}`}>
      {/* pruh při tahu doprava: zelené „Hotovo" / v Historii modré „Zpět" (jen z klidu) */}
      <div className={`absolute inset-y-0 left-0 flex items-center gap-1.5 pl-4 text-white font-bold text-sm ${done ? "bg-brand" : "bg-success"}`} style={{ width: fromRest.current ? Math.max(0, dx) : 0 }}>
        {fromRest.current && dx > 40 && (done ? <><Undo2 size={18} /> Zpět</> : <><CheckCircle2 size={18} /> Hotovo</>)}
      </div>
      {/* pás akcí vpravo — schová se při tahu doprava i v režimu mazání */}
      <div className="absolute inset-y-0 right-0 flex transition-opacity" data-testid={`swipe-actions-${t.id}`}
        style={{ opacity: dx > 0 ? 0 : 1, pointerEvents: dx > 0 ? "none" : undefined }}>
        {actions.map((a) => (
          <button key={a.key} onClick={(e) => { e.stopPropagation(); markSwiped(); setOpenId(null); setDx(0); a.fn(); }}
            data-testid={`swipe-${a.key}-${t.id}`}
            className={`flex flex-col items-center justify-center gap-0.5 ${a.cls}`} style={{ width: ACTION_W }}>
            <a.Icon size={17} /><span className="text-[10px] font-bold">{a.label}</span>
          </button>
        ))}
      </div>
      {/* vlastní obsah řádku */}
      <div ref={rowRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onClick={handleClick}
        data-testid={`list-row-${t.id}`}
        style={{ transform: `translateX(${dx}px)` }}
        className={`relative flex items-start gap-3 px-4 py-3.5 bg-secondary border border-border/70 rounded-[16px] touch-pan-y select-none ${noAnim ? "" : "transition-transform duration-200"} motion-reduce:transition-none`}>
        {!done && (
          <button onClick={(e) => { e.stopPropagation(); onComplete(t, e.currentTarget); }} data-testid={`list-complete-${t.id}`}
            className="shrink-0 self-start mt-0.5 text-muted-foreground">
            <Circle size={20} />
          </button>
        )}
        <RowBody t={t} done={done} isDelegated={isDelegated} />
        {done && <TaskStatusBadge value={t.status} />}
      </div>
    </div>
  );
}

// 360: groups = předpočítané skupiny rozpracovaných (Úkoly › Seznam na telefonu); bez nich se skupiny spočítají z tasks.
export function DeadlineList({ tasks, groups: pre, mobile, onOpen, onComplete, canComplete, isDelegated, swipe, by = "deadline" }) {
  const groups = pre || groupTasks(tasks, by);   // 350: termín (výchozí), priorita nebo zakázka
  if (!groups.length) return null;
  return (
    <div className="space-y-4" data-testid="deadline-list">
      {groups.map((g) => (
        <div key={g.key} data-testid={`list-group-${g.key}`}>
          {!mobile && <h2 className={`font-heading uppercase tracking-widest text-[11.5px] mb-2 ${g.danger ? "text-destructive" : "text-muted-foreground"}`}>{g.label} ({g.items.length})</h2>}
          {mobile ? (
            <div className={`bg-card border rounded-[16px] p-3 space-y-2 ${g.danger ? "border-destructive/35" : "border-border"}`}>
              <h2 className={`font-heading uppercase tracking-widest text-[11.5px] px-1 ${g.danger ? "text-destructive" : "text-muted-foreground"}`}>{g.label} ({g.items.length})</h2>
              {g.items.map((t) => (
                <SwipeRow key={t.id} t={t} isDelegated={isDelegated} onOpen={onOpen} onComplete={onComplete}
                  canEdit={swipe.canEdit(t)} canIdentity={swipe.canIdentity(t)} canDelete={swipe.canDelete(t)}
                  onToday={swipe.onToday} onPriority={swipe.onPriority} onEdit={swipe.onEdit} onDelete={swipe.onDelete}
                  openId={swipe.openId} setOpenId={swipe.setOpenId} />
              ))}
            </div>
          ) : (
            <div className={`bg-card border rounded-[16px] overflow-hidden ${g.danger ? "border-destructive/35" : "border-border"}`}>
              {g.items.map((t) => (
                <ListRow key={t.id} t={t} onOpen={onOpen} onComplete={onComplete} canComplete={canComplete(t)} isDelegated={isDelegated} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// 360: telefon — záložka Hotové zmizela; Ke schválení a Hotové jsou sbalitelné skupiny pod rozpracovanými
// (tah doprava = Vrátit mezi rozpracované, jako dřív v Historii). Hotové jsou na začátku sbalené.
export function EndGroups({ groups, collapsed, onToggle, isDelegated, onOpen, onReopen, canReopen, canDelete, onDelete, openId, setOpenId, more }) {
  if (!groups.length) return null;
  return (
    <div className="space-y-4" data-testid="end-groups">
      {groups.map((g) => {
        const col = collapsed(g.key);
        return (
          <div key={g.key} className="bg-card border border-border rounded-[16px] p-3 space-y-2" data-testid={`list-group-${g.key}`}>
            <button type="button" onClick={() => onToggle(g.key)} aria-expanded={!col} data-testid={`task-group-toggle-${g.key}`}
              className={`w-full flex items-center gap-1.5 px-1 min-h-[28px] font-heading uppercase tracking-widest text-[11.5px] ${g.key === "done" ? "text-success" : "text-amber-600 dark:text-amber-400"}`}>
              <ChevronDown size={14} className={`transition-transform motion-reduce:transition-none ${col ? "-rotate-90" : ""}`} />{g.label} ({g.count ?? g.items.length})
            </button>
            {!col && g.items.map((t) => (
              <SwipeRow key={t.id} t={t} done isDelegated={isDelegated} onOpen={onOpen} onReopen={onReopen} canReopen={canReopen(t)}
                canDelete={canDelete(t)} onDelete={onDelete} openId={openId} setOpenId={setOpenId} />
            ))}
            {!col && g.key === "done" && more}
          </div>
        );
      })}
    </div>
  );
}
